import { QRCodeCanvas } from 'qrcode.react';
import { formatarDataHora } from '@/pages/pedido/utils';
import { NAVY, OURO } from '@/pages/loja/identidade';
import { gerarTicketIngresso, salvarOuCompartilhar } from '../utils';
import { gerarConvite } from '@/lib/conviteImagem';
import type { IngressoCarteira } from '@/pages/loja/types';

interface Props {
  ingresso: IngressoCarteira;
  eventoNome: string;
  eventoInicio: string | null;
  eventoLocal: string | null;
  aoPagarPendente: (pedidoCodigo: string) => void;
}

// O ingresso dentro da carteira escura: cartão com borda clara, QR em caixa
// branca (para ler no celular do portaria) e ações em dourado.
export default function CartaoIngresso({
  ingresso,
  eventoNome,
  eventoInicio,
  eventoLocal,
  aoPagarPendente,
}: Props) {
  const pendente = ingresso.status === 'pendente';
  const dataHora = eventoInicio ? formatarDataHora(eventoInicio) : '';
  const qrId = `qr-carteira-${ingresso.codigo}`;

  const nomeArquivo = `ingresso-${(ingresso.nome || 'ingresso').trim().replace(/\s+/g, '-').toLowerCase()}.png`;
  const textoComprovante = [
    `Ingresso · ${eventoNome}`,
    `${dataHora}${eventoLocal ? ` · ${eventoLocal}` : ''}`,
    `Portador: ${ingresso.nome}`,
    `${ingresso.modalidade}`,
  ].join('\n');

  async function baixar() {
    const qrCanvas = document.getElementById(qrId) as HTMLCanvasElement | null;
    if (!qrCanvas) return;
    await document.fonts.ready;
    let canvas: HTMLCanvasElement;
    try {
      canvas = await gerarConvite({
        evento: eventoNome,
        quando: dataHora,
        local: eventoLocal,
        rotulo: 'Ingresso',
        nome: ingresso.nome,
        detalhe: ingresso.modalidade,
        codigo: ingresso.codigo,
      });
    } catch {
      canvas = gerarTicketIngresso({
        evento: eventoNome,
        dataHora,
        local: eventoLocal,
        portador: ingresso.nome,
        participacao: ingresso.modalidade,
        qrCanvas,
      });
    }
    await salvarOuCompartilhar(canvas, nomeArquivo, textoComprovante);
  }

  async function enviarWhats() {
    const qrCanvas = document.getElementById(qrId) as HTMLCanvasElement | null;
    if (!qrCanvas) return;
    await document.fonts.ready;
    let canvas: HTMLCanvasElement;
    try {
      canvas = await gerarConvite({
        evento: eventoNome,
        quando: dataHora,
        local: eventoLocal,
        rotulo: 'Ingresso',
        nome: ingresso.nome,
        detalhe: ingresso.modalidade,
        codigo: ingresso.codigo,
      });
    } catch {
      canvas = gerarTicketIngresso({
        evento: eventoNome,
        dataHora,
        local: eventoLocal,
        portador: ingresso.nome,
        participacao: ingresso.modalidade,
        qrCanvas,
      });
    }
    const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/png'));
    const arquivo = blob ? new File([blob], nomeArquivo, { type: 'image/png' }) : null;

    if (arquivo && navigator.canShare?.({ files: [arquivo] })) {
      try {
        await navigator.share({ files: [arquivo], title: eventoNome, text: textoComprovante });
        return;
      } catch (e) {
        if ((e as Error).name === 'AbortError') return;
      }
    }
    const texto = `${textoComprovante}\nMostre este QR na entrada: ${window.location.href}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(texto)}`, '_blank', 'noopener,noreferrer');
  }

  return (
    <div
      className={`overflow-hidden rounded-[16px] border border-white/10 bg-white/[0.05] ${
        pendente ? 'opacity-60' : ''
      }`}
    >
      <div className="p-5">
        <div className="text-center">
          <p className="text-[13px] font-bold uppercase tracking-[0.06em] text-white">
            {eventoNome}
          </p>
          {dataHora && <p className="mt-0.5 text-[13px] text-white/60">{dataHora}</p>}
          {eventoLocal && <p className="mt-0.5 text-[12px] text-white/45">{eventoLocal}</p>}
        </div>

        <div className="relative mt-4 flex justify-center">
          <div className="rounded-[14px] bg-white p-2">
            <QRCodeCanvas
              id={qrId}
              value={ingresso.codigo}
              size={168}
              bgColor="#ffffff"
              fgColor="#000000"
              level="M"
              marginSize={2}
            />
          </div>
        </div>

        <div className="mt-4 border-t border-white/10 pt-3 text-center">
          <p className="text-[15px] font-semibold text-white">{ingresso.nome}</p>
          <p className="mt-0.5 text-[13px] text-white/55">{ingresso.modalidade}</p>
        </div>

        {pendente ? (
          <div className="mt-4 text-center">
            <p className="text-[12px] text-white/45">aguardando pagamento</p>
            <button
              type="button"
              onClick={() => aoPagarPendente(ingresso.pedido_codigo)}
              className="aperta mt-2 h-11 w-full cursor-pointer whitespace-nowrap rounded-[12px] border border-white/15 text-[13.5px] font-semibold text-white/85 transition-colors hover:bg-white/[0.06]"
            >
              Pagar agora
            </button>
          </div>
        ) : (
          <div className="mt-4 flex gap-2">
            <button
              type="button"
              onClick={baixar}
              className="aperta flex h-11 flex-1 cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap rounded-[12px] text-[13.5px] font-bold"
              style={{ background: OURO, color: NAVY }}
            >
              <span className="flex h-4 w-4 items-center justify-center">
                <i className="ri-download-2-line" aria-hidden />
              </span>
              Baixar
            </button>
            <button
              type="button"
              onClick={enviarWhats}
              className="aperta flex h-11 flex-1 cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap rounded-[12px] bg-[#25D366] text-[13.5px] font-bold text-white transition-colors hover:brightness-95"
            >
              <span className="flex h-4 w-4 items-center justify-center">
                <i className="ri-whatsapp-line" aria-hidden />
              </span>
              WhatsApp
            </button>
          </div>
        )}
      </div>
    </div>
  );
}